import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import OfferAdditionalDataCard from '../components/OfferAdditionalDataCard';
import { useMutation } from '@apollo/client';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { OFFER_CLIENT_FACILITIES_INPUT_MUTATION } from '../mutations';
import { OFFER_CLIENT_FACILITIES_RETRIEVED } from '../subscriptions';
import {
  INVOICING_EXCHANGE_RATES_QUERY,
  ACQUISITION_EXCHANGE_RATE_QUERY,
  CREDIT_FINANCING_LINE_QUERY,
  INVOICING_BANK_QUERY,
  SCHEDULE_TYPE_QUERY,
  INTEREST_TYPE_QUERY,
  OFFER_TYPE_QUERY,
  FINANCIAL_PRODUCT_RULE_OUTPUT_QUERY
} from '../queries';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { showError } from 'actions/actionCreators/snackbarActions';
import { useDictionary } from '../hooks/useDictionary';
import { useServerErrorHandling } from 'utils/hooks/useServerErrorHandling';
import { get } from '@totalsoft/change-tracking-react';

const OfferAdditionalDataCardContainer = ({
  saving,
  documentId,
  siteId,
  documentTypeId,
  onPropertyChange,
  onSave,
  isDirty,
  validation,
  variableInterestComputing,
  handleFacilityStateChange,
  isFacilityFrame,
  onInterestVariablePercentRefresh,
  cardExpanded,
  onExpand,
  lens,
  additionalDataLoaded,
  offerHeaderLoading
}) => {
  const [offerClientFacilitiesOptions, setClientFacilityOptions] = useState({ facilityList: [] });
  const [offerClientFacilities, setOfferClientFacilities] = useState([]);

  const invoicingExchangeRateData = useDictionary(INVOICING_EXCHANGE_RATES_QUERY, { skip: !cardExpanded });
  const acquisitionExchangeRateData = useDictionary(ACQUISITION_EXCHANGE_RATE_QUERY, { skip: !cardExpanded });
  const creditFinancingLineData = useDictionary(CREDIT_FINANCING_LINE_QUERY, { skip: !cardExpanded });
  const invoicingBanks = useDictionary(INVOICING_BANK_QUERY, { skip: !cardExpanded });
  const scheduleTypeData = useDictionary(SCHEDULE_TYPE_QUERY, { skip: !cardExpanded });
  const interestTypeData = useDictionary(INTEREST_TYPE_QUERY, { skip: !cardExpanded });
  const offerTypeData = useDictionary(OFFER_TYPE_QUERY, {
    variables: { documentTypeId },
    skip: !cardExpanded || !documentTypeId
  });
  const { data: financialProductRuleOutputData } = useQueryWithErrorHandling(FINANCIAL_PRODUCT_RULE_OUTPUT_QUERY, {
    variables: { documentId, siteId },
    skip: !cardExpanded
  });
  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      setOfferClientFacilities(false);
    }
  });

  const loadClientFacilities = () => {
    saveOfferClientFacilities({
      variables: {
        input: {
          documentId,
          siteId
        }
      }
    });
  };

  const openLinkedDocument = (documentId, documentSiteId) => e => {
    e.stopPropagation();
    if (isFacilityFrame) window.open(`/facilities/${documentId}/${documentSiteId}/generalData`, '_blank');
  };

  useEffect(() => {
    setClientFacilityOptions({
      facilityList: [
        {
          chDocumentId: lens.additionalData?.clientFacilityId |> get,
          documentNumber: lens.additionalData?.clientFacilityNumber |> get
        }
      ]
    });
  }, [lens.additionalData.clientFacilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (offerClientFacilities) setClientFacilityOptions({ ...offerClientFacilitiesOptions, ...offerClientFacilities });
  }, [offerClientFacilities]); // eslint-disable-line react-hooks/exhaustive-deps

  const [saveOfferClientFacilities] = useMutation(OFFER_CLIENT_FACILITIES_INPUT_MUTATION, {
    skip: !cardExpanded,
    onError: error => {
      showError(error.message);
      setOfferClientFacilities(false);
    },
    onCompleted: r => {
      const { correlationId } = r.offerClientFacilities;
      storeKey({ correlationId });
    }
  });

  useGlobalSubscription(OFFER_CLIENT_FACILITIES_RETRIEVED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      setOfferClientFacilities(data.offerClientFacilitiesRetrieved);
    }
  });

  return (
    <OfferAdditionalDataCard
      onPropertyChange={onPropertyChange}
      invoicingExchangeRates={invoicingExchangeRateData.data}
      acquisitionExchangeRates={acquisitionExchangeRateData.data}
      creditFinancingLines={creditFinancingLineData.data}
      scheduleTypes={scheduleTypeData.data}
      interestTypes={interestTypeData.data}
      offerTypes={offerTypeData.data}
      offerFinancialProductRuleReadOnlyFields={financialProductRuleOutputData?.financialProductRuleOutputData ?? {}}
      bankList={invoicingBanks.data}
      offerClientFacilities={offerClientFacilitiesOptions}
      loadClientFacilities={loadClientFacilities}
      validation={validation}
      onSave={onSave}
      saving={saving}
      isDirty={isDirty}
      variableInterestComputing={variableInterestComputing}
      handleFacilityStateChange={handleFacilityStateChange}
      isFacilityFrame={isFacilityFrame}
      openLinkedDocument={openLinkedDocument}
      onInterestVariablePercentRefresh={onInterestVariablePercentRefresh}
      cardExpanded={cardExpanded}
      onExpand={onExpand}
      lens={lens}
      additionalDataLoaded={additionalDataLoaded}
      offerHeaderLoading={offerHeaderLoading}
    />
  );
};

OfferAdditionalDataCardContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onExpand: PropTypes.func.isRequired,
  cardExpanded: PropTypes.bool.isRequired,
  isDirty: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  validation: PropTypes.object,
  variableInterestComputing: PropTypes.bool.isRequired,
  documentTypeId: PropTypes.number,
  handleFacilityStateChange: PropTypes.func,
  isFacilityFrame: PropTypes.bool,
  onInterestVariablePercentRefresh: PropTypes.func.isRequired,
  lens: PropTypes.object.isRequired,
  additionalDataLoaded: PropTypes.bool.isRequired,
  offerHeaderLoading: PropTypes.bool.isRequired
};

export default OfferAdditionalDataCardContainer;
